/**
 * InlineSearch · Compact search bar for listing pages.
 *
 * Features:
 *   - Debounce 200ms before calling onChange
 *   - "/" keyboard shortcut focuses the input (when not already in a text field)
 *   - "X" clear button
 *   - Count label "12 de 47" when filtering
 *   - Design tokens from DESIGN.md
 *   - Mobile-first; keyboard shortcut gated to `pointer: fine` devices
 *
 * Usage:
 *   <InlineSearch
 *     value={search}
 *     onChange={setSearch}
 *     placeholder="Buscar projetos…"
 *     total={data.length}
 *     filtered={filtered.length}
 *   />
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

// Inline SVG icons (no external icon dep)
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden className={className}>
      <circle cx="6.5" cy="6.5" r="4" />
      <path d="M11 11l2.5 2.5" strokeLinecap="round" />
    </svg>
  );
}
function ClearIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M2 2l6 6M8 2l-6 6" strokeLinecap="round" />
    </svg>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface InlineSearchProps {
  /** Current controlled value (call site owns the state). */
  value: string;
  /** Called with the debounced new value. */
  onChange: (v: string) => void;
  placeholder?: string;
  /** Total items in the unfiltered list (for the count label). */
  total?: number;
  /** Items currently visible after filtering. */
  filtered?: number;
  /** Extra wrapper class. */
  className?: string;
  /** Debounce delay in ms (default 200). */
  debounceMs?: number;
  /** Whether to register the "/" shortcut to focus this input (default true). */
  slashFocus?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InlineSearch({
  value,
  onChange,
  placeholder = 'Buscar…',
  total,
  filtered,
  className,
  debounceMs = 200,
  slashFocus = true,
}: InlineSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Internal draft (pre-debounce) so the input feels instant.
  const [draft, setDraft] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync external value → draft when parent resets it.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setDraft(raw);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onChange(raw), debounceMs);
    },
    [onChange, debounceMs],
  );

  const handleClear = useCallback(() => {
    setDraft('');
    onChange('');
    inputRef.current?.focus();
  }, [onChange]);

  // "/" shortcut — only on pointer:fine devices (desktop/trackpad).
  useEffect(() => {
    if (!slashFocus) return;
    // Gate to fine-pointer devices (keyboard shortcuts don't apply on touch).
    const isFinePointer =
      typeof window !== 'undefined' &&
      window.matchMedia('(pointer: fine)').matches;
    if (!isFinePointer) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase() ?? '';
      const editable = (e.target as HTMLElement)?.isContentEditable;
      // Already in a text field — let the character type normally.
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || editable) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [slashFocus]);

  // Cleanup debounce on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const showCount =
    draft.trim().length > 0 &&
    typeof filtered === 'number' &&
    typeof total === 'number';

  return (
    <div
      className={cn(
        'flex items-center gap-2',
        className,
      )}
    >
      <div className="relative flex-1 min-w-0">
        {/* Search icon */}
        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none w-3.5 h-3.5" />

        <input
          ref={inputRef}
          type="search"
          value={draft}
          onChange={handleChange}
          placeholder={placeholder}
          aria-label={placeholder}
          className={cn(
            // Layout
            // Wave audit-gap · 2026-06-03 — h-8 (32px) → h-11 (44px) touch target,
            // consistent with Button's 44pt standard (OPERATING-MANUAL).
            'w-full h-11 pl-8 pr-8 rounded-lg text-[13px]',
            // Color tokens
            'bg-surface-2 border border-line text-ink placeholder:text-ink-soft',
            // Focus ring
            'focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30',
            // Transition
            'transition-colors duration-100 ease-out',
            // Clear browser default search cancel button
            '[&::-webkit-search-cancel-button]:hidden',
          )}
        />

        {/* Clear button */}
        {draft.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Limpar busca"
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2',
              'flex items-center justify-center w-4 h-4 rounded-full',
              'text-ink-soft hover:text-ink hover:bg-surface-3',
              'transition-colors duration-100 ease-out',
            )}
          >
            <ClearIcon size={10} />
          </button>
        )}
      </div>

      {/* Count label */}
      {showCount && (
        <span
          aria-live="polite"
          aria-atomic="true"
          className="shrink-0 text-[11px] text-ink-muted tabular-nums whitespace-nowrap"
        >
          {filtered} de {total}
        </span>
      )}
    </div>
  );
}
